import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import PropTypes from "prop-types";

// import onClickCTAScrollTo from "../../utils/onClickCTAScrollTo";
import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import isSafari from "@/next-src/utils/isSafari";

import blockCSS from "./VideoBgBase.module.scss";
import ResponsiveImage from "@/next-src/components/ui/responsive-image/ResponsiveImage";
import ButtonLink from "@/next-src/components/ui/button-link/ButtonLink";

const VideoBgBase = ({
  block,
  className = "",
  showSendUsMessage,
  isMobile = false,
  isFirst = false,
}) => {
  const sectionRef = useRef(null);
  const [elOptions, setElOptions] = useState({
    height: 0,
    innerElTop: "unset",
    innerElBottom: "0px",
  });
  const {
    titleMarginTop,
    titleMarginX,
    textMarginTop,
    textMarginX,
    ratingTextMarginTop,
    ratingTextMarginX,
    buttonsMarginTop,
    buttonsMarginX,
    mediaMode,
    img,
    videoSrc,
    mediaHeight,
    mediaPlacementMode,
    bgColor,
    button,
    secondButton,
  } = block || {};

  let { title, text, ratingText, isMediaParallax } = block;
  title = convertHTMLtoJSX(title);
  text = convertHTMLtoJSX(text);
  ratingText = convertHTMLtoJSX(ratingText);
  const btnMR =
    buttonsMarginX[1] === 0 ? "auto" : `${100 - buttonsMarginX[1]}%`;
  const btnML = buttonsMarginX[0] === 100 ? "auto" : `${buttonsMarginX[0]}%`;
  isMediaParallax = isMobile
    ? mediaPlacementMode === "background" && isMediaParallax
    : isMediaParallax;

  useEffect(() => {
    // only execute all the code below in client side
    if (typeof window !== "undefined") {
      // Handler to call on window resize
      const onScroll = () => {
        const el = sectionRef.current;
        if (!el) return;
        const { top, bottom } = el.getBoundingClientRect();
        let wHeight =
          window.innerWidth >= 992 ? window.innerHeight : window.outerHeight;
        if (isSafari()) {
          wHeight = window.innerHeight;
        }
        const additionalHeight = window.innerWidth < 992 ? 60 : 50;
        if (wHeight >= top && top > 0) {
          setElOptions({
            height: `${wHeight - Math.min(top, wHeight) + additionalHeight}px`,
            innerElTop: "auto",
            innerElBottom: `0`,
          });
        } else if (top < 0 && bottom <= wHeight) {
          setElOptions({
            height: `${Math.max(bottom, 0) + additionalHeight}px`,
            innerElTop: "0",
            innerElBottom: `auto`,
          });
        } else if (top <= 0 && bottom >= wHeight) {
          setElOptions({
            height: `${wHeight + additionalHeight}px`,
            innerElTop: "0",
            innerElBottom: `auto`,
          });
        } else {
          setElOptions({
            height: "0px",
            innerElTop: "unset",
            innerElBottom: "0px",
          });
        }
      };

      // Add event listener
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);

      // Call handler right away so state gets updated with initial window size
      setTimeout(() => {
        onScroll();
      }, 500);
      onScroll();

      // Remove event listener on cleanup
      return () => {
        window.removeEventListener("scroll", onScroll, { passive: true });
        window.removeEventListener("resize", onScroll);
      };
    }
  }, [sectionRef]); // Empty array ensures that effect is only run on mount

  if (!block) return null;

  const onClickCTA =
    ({ enableScrollTo, enableShowMsgUs }) =>
    (e) => {
      if (enableShowMsgUs) {
        showSendUsMessage();
        e.preventDefault();
      } else if (enableScrollTo) {
        // return onClickCTAScrollTo(enableScrollTo)(e);
      }
    };

  const getBgColor = () => {
    const isRegularMobileSection =
      isMobile && mediaPlacementMode !== "background";
    return isRegularMobileSection && bgColor === "rgba(0,0,0,0)"
      ? "#FAF9F7"
      : bgColor || "";
  };

  const minHeight = isMobile ? 300 : 800;

  return (
    <section
      ref={sectionRef}
      className={cn(className, blockCSS.section)}
      style={{
        minHeight: isMediaParallax ? "100vh" : `${mediaHeight || minHeight}px`,
        backgroundColor: getBgColor(),
      }}
    >
      <div
        className={cn(blockCSS.container, {
          [blockCSS.containerImgBgMode]: isMobile
            ? mediaPlacementMode === "background"
            : true,
          [blockCSS.containerReversed]:
            isMobile && mediaPlacementMode === "last",
        })}
      >
        <div
          className={cn(blockCSS.banner, {
            [blockCSS.isParallax]: isMediaParallax,
            [blockCSS.isFirst]: isFirst && !isMediaParallax,
          })}
          style={
            isMediaParallax
              ? {
                  height: elOptions.height,
                  top: elOptions.innerElTop,
                  bottom: elOptions.innerElBottom,
                }
              : {}
          }
        >
          {mediaMode === "video" && (
            <video
              loop
              muted
              autoPlay
              playsInline
              src={videoSrc}
              preload="auto"
              style={{
                height: isMediaParallax
                  ? "100vh"
                  : `${mediaHeight || minHeight}px`,
                ...(isMediaParallax && {
                  top: elOptions.innerElTop,
                  bottom: elOptions.innerElBottom,
                  maxHeight: "100vh",
                  transition: "all 0.3s ease-in-out",
                }),
              }}
              width="100%"
              height="100%"
              crossOrigin="anonymous"
              className={cn(blockCSS.video)}
              controlsList="play timeline volume nofullscreen nodownload noremoteplayback noplaybackrate"
            />
          )}
          {mediaMode === "image" && (
            <ResponsiveImage
              alt={img?.alt}
              src={img?.src}
              sizes="100vw"
              q={isMobile ? 60 : 100}
              width={isMobile ? 384 : 2560}
              height={mediaHeight}
              style={{
                height: `${mediaHeight || 570}px`,
              }}
            />
          )}
        </div>
        <div className={cn(blockCSS.textBlock)}>
          {title && (
            <div
              className={cn(blockCSS.title)}
              style={{
                marginLeft: `${titleMarginX[0]}%`,
                marginRight: `${100 - titleMarginX[1]}%`,
                marginTop: `${titleMarginTop}px`,
              }}
            >
              {title}
            </div>
          )}
          {text && (
            <div
              className={cn("text-size-medium")}
              style={{
                marginLeft: `${textMarginX[0]}%`,
                marginRight: `${100 - textMarginX[1]}%`,
                marginTop: `${textMarginTop}px`,
              }}
            >
              {text}
            </div>
          )}
          {(!button?.hide || !secondButton?.hide) && (
            <div
              className={cn(blockCSS.btnWrapper)}
              style={{
                marginTop: `${buttonsMarginTop}px`,
                justifyContent:
                  btnML === "0%" && btnMR === "0%" ? "center" : "flex-start",
              }}
            >
              <div
                style={{
                  marginLeft: btnML,
                  marginRight: btnMR,
                }}
              >
                <ButtonLink
                  data={button}
                  className={cn("is-icon", {
                    "hero-mobile-btn-get-started": isMobile && isFirst,
                  })}
                />
                <ButtonLink data={secondButton} className="is-icon" />
              </div>
            </div>
          )}

          {ratingText && (
            <div
              className={cn("text-size-medium")}
              style={{
                marginLeft: `${ratingTextMarginX[0]}%`,
                marginRight: `${100 - ratingTextMarginX[1]}%`,
                marginTop: `${ratingTextMarginTop}px`,
              }}
            >
              {ratingText}
            </div>
          )}
          {/* {mediaPlacementMode === 'background' && (
            <div className={cn(blockCSS.bgGradient)} style={{ top: `${titleMarginTop - 50}px` }} />
          )} */}
        </div>
      </div>
    </section>
  );
};

VideoBgBase.propTypes = {
  block: PropTypes.shape({
    title: PropTypes.string,
    titleMarginTop: PropTypes.number,
    titleMarginX: PropTypes.arrayOf(PropTypes.number),
    text: PropTypes.string,
    textMarginTop: PropTypes.number,
    textMarginX: PropTypes.arrayOf(PropTypes.number),
    ratingText: PropTypes.string,
    ratingTextMarginTop: PropTypes.number,
    ratingTextMarginX: PropTypes.arrayOf(PropTypes.number),
    buttonsMarginTop: PropTypes.number,
    buttonsMarginX: PropTypes.arrayOf(PropTypes.number),
    mediaMode: PropTypes.string, // 'image' || 'video'
    imgSrc: PropTypes.string,
    imgAlt: PropTypes.string,
    videoSrc: PropTypes.string,
    mediaHeight: PropTypes.number,
    mediaPlacementMode: PropTypes.string,
    bgColor: PropTypes.string,
    button: PropTypes.shape({
      enableShowMsgUs: PropTypes.bool,
    }),
    secondButton: PropTypes.shape({
      enableShowMsgUs: PropTypes.bool,
    }),
    productId: PropTypes.string,
    isMediaParallax: PropTypes.bool,
    hideBtnPinnedBottomOnHero: PropTypes.bool, // check its usage
  }).isRequired,
  showSendUsMessage: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  isFirst: PropTypes.bool,
  className: PropTypes.string,
};

export default VideoBgBase;
